import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { KeyTerm, ImportantNote } from "../note-components";
import type { UnitNotes } from "../types";

const microUnit6Notes: UnitNotes = {
  unitId: "unit-6",
  unitName: "Unit 6: Market Failure and the Role of Government",
  examWeight: "8–13%",
  focus: "Market Inefficiency and Public Policy",
  sections: [
    {
      id: "topic-6-1",
      title: "Topic 6.1: Socially Efficient and Inefficient Market Outcomes",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ Social Efficiency (Allocative Efficiency)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              A market is socially efficient when resources are allocated in a way that maximizes <strong>Total Economic Surplus</strong> (Consumer Surplus + Producer Surplus).
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-900 mb-2">The Condition for Efficiency</h4>
              <BlockMath math="MSB = MSC" />
              <ul className="list-disc pl-5 space-y-2 text-sm text-slate-700 mt-2">
                <li><strong>MSB (Marginal Social Benefit):</strong> The total benefit to society from consuming one more unit.</li>
                <li><strong>MSC (Marginal Social Cost):</strong> The total cost to society of producing one more unit.</li>
              </ul>
            </div>
            <div className="bg-gradient-to-r from-slate-50 to-gray-50 p-4 rounded-lg border border-slate-200">
              <h4 className="font-bold text-slate-900">Perfect Competition</h4>
              <p className="text-sm text-slate-700 mt-1">
                In a perfectly competitive market with no externalities, Price (<InlineMath math="P" />) reflects <InlineMath math="MSB" /> and Marginal Cost (<InlineMath math="MC" />) reflects <InlineMath math="MSC" />. Thus, the equilibrium results in <InlineMath math="MSB = MSC" />.
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚠️ Market Failure</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Market failure occurs when the free market equilibrium quantity (<InlineMath math="Q_m" />) differs from the socially optimal quantity (<InlineMath math="Q_{opt}" />).
            </p>
            <div className="grid md:grid-cols-2 gap-3 mb-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">Rational Agents</h4>
                <p className="text-xs text-slate-700 mt-1">Firms and individuals make decisions based on <strong>private</strong> benefits (MPB) and costs (MPC), often ignoring social impacts.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">Deadweight Loss (DWL)</h4>
                <p className="text-xs text-slate-700 mt-1">Reduction in surplus from underproduction (<InlineMath math="MSB > MSC" />) or overproduction (<InlineMath math="MSC > MSB" />).</p>
              </div>
            </div>
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
              <h4 className="font-bold text-amber-900">Sources of Market Failure</h4>
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                <li><strong>Imperfect Competition:</strong> Monopolies/Oligopolies restrict output (<InlineMath math="P > MC" />).</li>
                <li><strong>Externalities:</strong> Private costs/benefits ≠ social costs/benefits.</li>
                <li><strong>Public Goods:</strong> The free-rider problem prevents private supply.</li>
                <li><strong>Asymmetric Information:</strong> One party has more info (e.g., used cars, insurance).</li>
              </ul>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-2",
      title: "Topic 6.2: Externalities",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏭 Negative Externalities (Spillover Costs)</h3>
            <p className="text-slate-700 mb-4">Costs imposed on a third party not involved in the transaction (e.g., pollution, loud noise).</p>
            <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-400 mb-4">
              <h4 className="font-bold text-red-900">The Analysis</h4>
              <ul className="text-sm text-slate-700 space-y-1 mt-1">
                <li><strong>Relationship:</strong> <InlineMath math="MSC > MPC" /> (MSC = MPC + External Cost)</li>
                <li><strong>Market Outcome:</strong> The market produces <strong>too much</strong> (<InlineMath math="Q_{market} > Q_{optimal}" />).</li>
                <li><strong>DWL:</strong> Triangle points toward the social optimum.</li>
                <li><strong>Correction:</strong> Per-unit <strong>Tax</strong> equal to the external cost (Internalizing the externality).</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💉 Positive Externalities (Spillover Benefits)</h3>
            <p className="text-slate-700 mb-4">Benefits created for a third party (e.g., vaccines, education, manicured lawns).</p>
            <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-400 mb-4">
              <h4 className="font-bold text-green-900">The Analysis</h4>
              <ul className="text-sm text-slate-700 space-y-1 mt-1">
                <li><strong>Relationship:</strong> <InlineMath math="MSB > MPB" /> (MSB = MPB + External Benefit)</li>
                <li><strong>Market Outcome:</strong> The market produces <strong>too little</strong> (<InlineMath math="Q_{market} < Q_{optimal}" />).</li>
                <li><strong>Correction:</strong> Per-unit <strong>Subsidy</strong> equal to the external benefit.</li>
              </ul>
            </div>
            <KeyTerm 
              term="Coase Theorem" 
              definition="If property rights are well-defined and transaction costs are low, private parties can bargain to solve externalities without government intervention."
            />
          </section>
        </div>
      )
    },
    {
      id: "topic-6-3",
      title: "Topic 6.3: Public and Private Goods",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📦 Classification of Goods</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Excludability</h4>
                <p className="text-sm text-slate-700">Can people be prevented from using the good if they don't pay?</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">Rivalry</h4>
                <p className="text-sm text-slate-700">Does one person's consumption reduce availability for others?</p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse border border-slate-300">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="border border-slate-300 p-2"></th>
                    <th className="border border-slate-300 p-2 text-slate-800">Excludable</th>
                    <th className="border border-slate-300 p-2 text-slate-800">Non-Excludable</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-slate-300 p-2 font-bold bg-slate-50 text-slate-800">Rival</td>
                    <td className="border border-slate-300 p-2 text-sm"><strong>Private Goods</strong> (Pizza, Clothing)</td>
                    <td className="border border-slate-300 p-2 text-sm"><strong>Common Resources</strong> (Fish in ocean, Clean air)</td>
                  </tr>
                  <tr>
                    <td className="border border-slate-300 p-2 font-bold bg-slate-50 text-slate-800">Non-Rival</td>
                    <td className="border border-slate-300 p-2 text-sm"><strong>Club Goods</strong> (Cable TV, Cinemas)</td>
                    <td className="border border-slate-300 p-2 text-sm"><strong>Public Goods</strong> (National Defense, Streetlights)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🚨 Failures in Public/Common Goods</h3>
            <div className="space-y-3">
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">Free-Rider Problem (Public Goods)</h4>
                <p className="text-sm text-slate-700 mt-1">Because goods are non-excludable, people use them without paying. Private firms can't profit, so government must provide them via taxes.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">Tragedy of the Commons (Common Resources)</h4>
                <p className="text-sm text-slate-700 mt-1">Rival but non-excludable goods are overused and depleted. <strong>Solutions:</strong> Regulation (quotas), privatization (property rights), or taxes.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-4",
      title: "Topic 6.4: The Effects of Government Intervention",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💸 Taxes and Subsidies</h3>
            <div className="space-y-4">
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <h4 className="font-bold text-indigo-900">Per-Unit Tax/Subsidy</h4>
                <ul className="list-disc pl-5 text-sm text-slate-700 mt-1">
                  <li>Affects <strong>MC, AVC, and ATC</strong>.</li>
                  <li>Changes the profit-maximizing quantity where <InlineMath math="MR = MC" />.</li>
                  <li><strong>Tax:</strong> MC shifts up → Q decreases → P increases.</li>
                </ul>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Lump-Sum Tax/Subsidy</h4>
                <ul className="list-disc pl-5 text-sm text-slate-700 mt-1">
                  <li>Affects <strong>Fixed Costs (FC) and ATC</strong>.</li>
                  <li>Does <strong>NOT</strong> change Marginal Cost (MC).</li>
                  <li>Firm produces the <strong>same quantity</strong> in the short run; only profit/loss changes.</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛑 Price Controls and Regulation</h3>
            <div className="grid md:grid-cols-2 gap-3 mb-6">
              <KeyTerm term="Price Ceiling" definition="Legal max price. Effective if below equilibrium. Creates a shortage." />
              <KeyTerm term="Price Floor" definition="Legal min price. Effective if above equilibrium. Creates a surplus." />
            </div>
            
            <h4 className="font-bold text-slate-800 mb-3">Regulating Natural Monopolies</h4>
            <div className="space-y-3">
              <div className="bg-cyan-50 p-4 rounded-lg border-l-4 border-cyan-400">
                <h4 className="font-bold text-cyan-900">Socially Optimal Price (P = MC)</h4>
                <p className="text-sm text-slate-700 mt-1">Allocatively efficient. However, because MC is often below ATC for natural monopolies, the firm incurs a loss and requires a government subsidy.</p>
              </div>
              <div className="bg-emerald-50 p-4 rounded-lg border-l-4 border-emerald-400">
                <h4 className="font-bold text-emerald-900">Fair Return Price (P = ATC)</h4>
                <p className="text-sm text-slate-700 mt-1">Break-even point where the firm earns <strong>Normal Profit</strong> (zero economic profit). No subsidy required. Q is higher than unregulated, but lower than socially optimal.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ Antitrust Policy</h3>
            <p className="text-slate-700 text-sm">
              Laws like the <strong>Sherman Antitrust Act</strong> designed to prevent monopolies, block anti-competitive mergers, and promote innovation and lower prices.
            </p>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-5",
      title: "Topic 6.5: Inequality",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📉 Measuring Inequality</h3>
            <div className="bg-white p-4 rounded-lg border border-slate-200 mb-4">
              <h4 className="font-bold text-slate-900">The Lorenz Curve</h4>
              <ul className="text-sm text-slate-700 mt-1 list-disc pl-5">
                <li><strong>X-axis:</strong> Cumulative % of population (poorest to richest).</li>
                <li><strong>Y-axis:</strong> Cumulative % of total income.</li>
                <li><strong>Line of Perfect Equality:</strong> 45-degree line. Inequality increases as the curve bows further away.</li>
              </ul>
            </div>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <h4 className="font-bold text-slate-900">Gini Coefficient</h4>
              <BlockMath math="\text{Gini} = \frac{\text{Area between Line of Equality and Lorenz Curve}}{\text{Total Area under Line of Equality}}" />
              <p className="text-xs text-center text-slate-600 mt-2">Scale 0 to 1: 0 = Perfect Equality | 1 = Perfect Inequality</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔍 Sources of Inequality</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="p-3 bg-blue-50 rounded border border-blue-100 text-center">
                <span className="text-xs font-bold text-blue-900 block">Human Capital</span>
                <p className="text-[10px] text-slate-600">Education & Skills</p>
              </div>
              <div className="p-3 bg-red-50 rounded border border-red-100 text-center">
                <span className="text-xs font-bold text-red-900 block">Discrimination</span>
                <p className="text-[10px] text-slate-600">Race, Gender, etc.</p>
              </div>
              <div className="p-3 bg-purple-50 rounded border border-purple-100 text-center">
                <span className="text-xs font-bold text-purple-900 block">Market Power</span>
                <p className="text-[10px] text-slate-600">Unions & CEOs</p>
              </div>
              <div className="p-3 bg-amber-50 rounded border border-amber-100 text-center">
                <span className="text-xs font-bold text-amber-900 block">Luck</span>
                <p className="text-[10px] text-slate-600">Inheritance</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏛️ Taxation and Transfers</h3>
            <div className="space-y-3">
              <div className="grid md:grid-cols-3 gap-3">
                <div className="bg-green-50 p-3 rounded border border-green-200">
                  <h5 className="font-bold text-green-900 text-sm">Progressive Tax</h5>
                  <p className="text-[11px] text-slate-700">Higher earners pay higher %. Reduces inequality (e.g., US Income Tax).</p>
                </div>
                <div className="bg-red-50 p-3 rounded border border-red-200">
                  <h5 className="font-bold text-red-900 text-sm">Regressive Tax</h5>
                  <p className="text-[11px] text-slate-700">Lower earners pay higher %. Increases inequality (e.g., Sales Tax).</p>
                </div>
                <div className="bg-gray-50 p-3 rounded border border-gray-200">
                  <h5 className="font-bold text-gray-900 text-sm">Proportional Tax</h5>
                  <p className="text-[11px] text-slate-700">Everyone pays the same % regardless of income (Flat Tax).</p>
                </div>
              </div>
              <ImportantNote>
                <strong>Transfer Payments:</strong> Government payments (Social Security, unemployment) for which no good/service is exchanged. These generally reduce inequality.
              </ImportantNote>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default microUnit6Notes;